import { useState, useCallback, useEffect } from 'react';
import type { Unit } from '../types';
import type mapboxgl from 'mapbox-gl';

export type DrawerContentType = 'unit_tooltip' | 'sm_cluster_tooltip' | 'lg_cluster_tooltip';

export interface UnitTooltipContent {
  type: 'unit_tooltip';
  unit: Unit;
}

export interface SmClusterTooltipContent {
  type: 'sm_cluster_tooltip';
  units: Unit[];
  coordinates: [number, number];
  initialActiveUnit?: Unit;
}

export interface LgClusterTooltipContent {
  type: 'lg_cluster_tooltip';
  units: Unit[];
  coordinates: [number, number];
}

export type DrawerContent = UnitTooltipContent | SmClusterTooltipContent | LgClusterTooltipContent;

export interface UseMobileDrawerReturn {
  isOpen: boolean;
  drawerContent: DrawerContent | null;
  dragProgress: number;
  openDrawer: (content: DrawerContent) => void;
  closeDrawer: () => void;
  handleDrag: (deltaY: number) => void;
  handleDragEnd: (velocityY: number) => void;
  mapRef?: React.RefObject<mapboxgl.Map | null>;
}

const DRAG_THRESHOLD = 50; // pixels
const VELOCITY_THRESHOLD = 0.5; // pixels per millisecond

export function useMobileDrawer(mapRef?: React.RefObject<mapboxgl.Map | null>): UseMobileDrawerReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<DrawerContent | null>(null);
  const [dragProgress, setDragProgress] = useState(0); // 0 = fully open, 1 = fully closed

  const openDrawer = useCallback((content: DrawerContent) => {
    setDrawerContent(content);
    setIsOpen(true);
    setDragProgress(0);

    // Prevent body scroll when drawer is open
    document.body.style.overflow = 'hidden';
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    setDragProgress(0);

    // Re-enable body scroll
    document.body.style.overflow = '';

    // Clear content after animation completes
    setTimeout(() => {
      setDrawerContent(null);
    }, 300);
  }, []);

  const handleDrag = useCallback((deltaY: number) => {
    if (!isOpen) return;

    // Swipe UP to close (deltaY is negative when swiping up)
    // Convert to progress: 0 = open, 1 = closed
    // Negative deltaY (swipe up) increases progress
    const progress = Math.max(0, Math.min(1, -deltaY / 200)); // 200px = full close distance
    setDragProgress(progress);
  }, [isOpen]);

  const handleDragEnd = useCallback((velocityY: number) => {
    if (!isOpen) return;

    // Fast upward flick (negative velocity) → close
    const fastFlick = velocityY < -VELOCITY_THRESHOLD;

    // Dragged up beyond threshold → close
    const draggedBeyondThreshold = dragProgress > (DRAG_THRESHOLD / 200);

    if (fastFlick || draggedBeyondThreshold) {
      closeDrawer();
    } else {
      // Snap back to open position
      setDragProgress(0);
    }
  }, [isOpen, dragProgress, closeDrawer]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeDrawer();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeDrawer]);

  return {
    isOpen,
    drawerContent,
    dragProgress,
    openDrawer,
    closeDrawer,
    handleDrag,
    handleDragEnd,
    mapRef,
  };
}
